import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TrashIcon } from '@radix-ui/react-icons';
import './Home.css';
import { GridMetadata, GridData } from '../services/firestoreService';
import { useUserStorage } from '../services/userDataStorage';
import ConfirmDialog from './ConfirmDialog';

interface HomeProps {
  onSidebarRefresh?: () => void;
}

const Home: React.FC<HomeProps> = ({ onSidebarRefresh }) => {
  const navigate = useNavigate();
  const [grids, setGrids] = useState<GridMetadata[]>([]);
  const [gridPreviews, setGridPreviews] = useState<{ [key: string]: GridData }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading your grids...');
  const userStorage = useUserStorage();
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    loadGrids();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStorage?.isFirebaseReady]);

  const loadGrids = async () => {
    try {
      if (!userStorage?.isFirebaseReady) {
        setInitialLoading(true);
        return;
      }

      const allGrids = await userStorage.getAllGrids();
      
      // Set initial loading to false once we have data
      setInitialLoading(false);
      
      // If no grids exist, create one and redirect to it
      if (allGrids.length === 0) {
        setIsLoading(true);
        const newGrid = await userStorage.createNewGrid();
        if (onSidebarRefresh) onSidebarRefresh();
        navigate(`/sheets/${newGrid.id}`, { replace: true });
        return;
      }
      
      // Sort by lastModified in descending order (most recent first)
      const sortedGrids = allGrids.sort((a, b) => b.lastModified - a.lastModified);
      setGrids(sortedGrids);

      // Load preview data for all grids
      setBackgroundLoading(true);
      const previews: { [key: string]: GridData } = {};
      
      // Load previews in parallel for better performance
      const previewPromises = sortedGrids.map(async (grid) => {
        try {
          const previewData = await userStorage.loadGridPreview(grid.id);
          if (previewData) {
            previews[grid.id] = previewData;
          }
        } catch (error) {
          console.error(`Error loading preview for ${grid.id}:`, error);
        }
      });
      
      await Promise.all(previewPromises);
      setGridPreviews(previews);
      setBackgroundLoading(false);
    } catch (error) {
      console.error('Error loading grids:', error);
      setIsLoading(false);
      setInitialLoading(false);
      setBackgroundLoading(false);
    }
  };

  const handleCreateNew = async () => {
    try {
      if (!userStorage) return;
      
      setIsLoading(true);
      setLoadingMessage('Creating new spreadsheet...');
      const newGrid = await userStorage.createNewGrid();
      if (onSidebarRefresh) onSidebarRefresh();
      navigate(`/sheets/${newGrid.id}`);
    } catch (error) {
      console.error('Error creating new grid:', error);
      setIsLoading(false);
    }
  };

  const handleOpenGrid = (id: string) => {
    navigate(`/sheets/${id}`);
  };

  const handleDeleteGrid = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Grid',
      message: 'Are you sure you want to delete this grid? This action cannot be undone.',
      onConfirm: () => confirmDeleteGrid(id)
    });
  };

  const confirmDeleteGrid = async (id: string) => {
    try {
      if (!userStorage) return;
      
      setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      setIsLoading(true);
      setLoadingMessage('Deleting spreadsheet...');
      
      await userStorage.deleteGrid(id);
      toast.success('Grid deleted successfully');
      
      // Quick refresh from cache
      const remainingGrids = await userStorage.getAllGrids();
      
      if (remainingGrids.length === 0) {
        setLoadingMessage('Creating new spreadsheet...');
        const newGrid = await userStorage.createNewGrid();
        if (onSidebarRefresh) onSidebarRefresh();
        navigate(`/sheets/${newGrid.id}`, { replace: true });
      } else {
        setLoadingMessage('Loading your grids...');
        await loadGrids();
        if (onSidebarRefresh) onSidebarRefresh();
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error deleting grid:', error);
      toast.error('Failed to delete grid. Please try again.');
      setLoadingMessage('Loading your grids...');
      await loadGrids();
      if (onSidebarRefresh) onSidebarRefresh();
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return "Updated just now";
    } else if (diffMinutes < 60) {
      return `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays === 1) {
      return "Updated 1 day ago";
    } else if (diffDays < 7) {
      return `Updated ${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Updated ${weeks} week${weeks === 1 ? '' : 's'} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `Updated ${months} month${months === 1 ? '' : 's'} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `Updated ${years} year${years === 1 ? '' : 's'} ago`;
    }
  };

  const getColumnHeader = (col: any, index: number): string => {
    if (index === 0) {
      return '#';
    }
    return col.name || '';
  };

  if (initialLoading || (!userStorage?.isFirebaseReady)) {
    return (
      <div className="home-container">
        <div className="home-header">
          <h1>Home</h1>
          <button className="create-new-btn" onClick={handleCreateNew} disabled>
            + New Spreadsheet
          </button>
        </div>
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading your grids...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="home-container">
        <div className="home-header">
          <h1>Home</h1>
          <button className="create-new-btn" onClick={handleCreateNew}>
            + New Spreadsheet
          </button>
        </div>
        <div className="loading-container">
          <div className="spinner" />
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>Home</h1>
        <button className="create-new-btn" onClick={handleCreateNew}>
          + New Spreadsheet
        </button>
      </div>
      
      <div className="spreadsheets-grid">
        {grids.map((grid) => {
          const preview = gridPreviews[grid.id];
          
          return (
            <div 
              key={grid.id}
              className="spreadsheet-card"
              onClick={() => handleOpenGrid(grid.id)}
            >
              <div className="card-header">
                <h3 className="spreadsheet-title">{grid.title}</h3>
                <button 
                  className="delete-btn"
                  onClick={(e) => handleDeleteGrid(grid.id, e)}
                  title="Delete grid"
                >
                  <TrashIcon className="delete-icon" />
                </button>
              </div>
              
              <div className="spreadsheet-preview">
                {preview ? (
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {preview.columnConfigs.slice(0, 5).map((col, index) => (
                          <th key={col.id}>{getColumnHeader(col, index + 1)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.gridData.slice(0, 8).map((row, rowIndex) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          {preview.columnConfigs.slice(0, 5).map((col) => (
                            <td key={col.id}>
                              {String(row[col.id] || '').slice(0, 15)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : backgroundLoading ? (
                  <div className="loading-preview">
                    <div className="preview-spinner"></div>
                    <p>Loading preview...</p>
                  </div>
                ) : (
                  <div className="empty-preview">
                    <p>Failed to load preview</p>
                  </div>
                )}
              </div>
              
              <div className="card-footer">
                <span className="last-modified">
                  {formatDate(grid.lastModified)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default Home; 